/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import msdyn365Commerce, { IActionContext, IDictionary, IObservableAction } from '@msdyn365-commerce/core-internal';
import { ICache } from '@msdyn365-commerce/cache-internal';
import { IProductList } from '@msdyn365-commerce/retail-proxy/dist/data-types';
import { ProductSearchResult } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { EXCEPTION_PRODUCT_LIST_ACTION, INVALID_PRODUCT_LIST_TYPE } from '@msdyn365-commerce/telemetry-internal';
import { toJS } from 'mobx';
import { set as _set } from 'lodash';
import { MODULE_CONFIG_KEY } from '../consts';
import { IProductListMetaData, ProductListInput, ProductListType } from './hydrator-action-inputs';
import { getProductsByCategoryListAction } from './product-list-actions/products-by-category.action';
import { getProductsByRecommendationListAction } from './product-list-actions/products-by-recommendation.action';
import { getProductsByRelationshipIdListAction } from './product-list-actions/products-by-relationship-id.action';
import { getProductsBySearchListAction } from './product-list-actions/products-by-search.action';
import { shouldOptimizeHydrate } from '../utils/helpers';

/**
 * Method to hydrate a product list from metadata provided by CMS
 * @param listMetaData The metadata for the product list from CMS
 */
export const hydrateProductList = async (
    listMetaData: IProductListMetaData,
    context: IActionContext,
    moduleId?: string,
    propertyToMapInConfig?: string
): Promise<void> => {
    let action: IObservableAction<ProductSearchResult[]> | undefined;
    switch (ProductListType[listMetaData.listType]) {
        case ProductListType.productBySearch:
            action = getProductsBySearchListAction;
            break;
        case ProductListType.productByCategory:
            action = getProductsByCategoryListAction;
            break;
        case ProductListType.relatedProducts:
            action = getProductsByRelationshipIdListAction;
            break;
        case ProductListType.editorial:
        case ProductListType.algo:
        case ProductListType.recommendation:
            action = getProductsByRecommendationListAction;
            break;
        default:
            context.telemetry.error(INVALID_PRODUCT_LIST_TYPE, listMetaData.listType);
            (<IProductList>(<unknown>listMetaData)).products = [];
            (<IProductList>(<unknown>listMetaData)).listMetadata = { error: INVALID_PRODUCT_LIST_TYPE };
    }

    if (action !== undefined) {
        return action(new ProductListInput(listMetaData, context), context)
            .then(productList => {
                (<IProductList>(<unknown>listMetaData)).products = productList;
                updateListDataInRequestCache(context, productList, moduleId, propertyToMapInConfig);
            })
            .catch(error => {
                context.telemetry.error(EXCEPTION_PRODUCT_LIST_ACTION);
                context.telemetry.exception(error);
                (<IProductList>(<unknown>listMetaData)).products = [];
                (<IProductList>(<unknown>listMetaData)).listMetadata = { error: EXCEPTION_PRODUCT_LIST_ACTION };
                updateListDataInRequestCache(context, [], moduleId, propertyToMapInConfig);
            });
    }

    return;
};

/**
 * update list data in request cache when client optimize hydrate is enabled. This
 * @param context
 * @param products
 * @param moduleId
 * @param propertyToMapInConfig
 */
const updateListDataInRequestCache = (
    context: IActionContext,
    products: ProductSearchResult[],
    moduleId?: string,
    propertyToMapInConfig?: string
) => {
    if (msdyn365Commerce.isBrowser && shouldOptimizeHydrate(context.requestContext) && moduleId && propertyToMapInConfig) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const requestCache: ICache = (<any>context).requestCache;
        const cacheValue = requestCache.get<IDictionary<{ products: ProductSearchResult[] }>>({
            typeName: MODULE_CONFIG_KEY,
            key: moduleId
        });
        if (cacheValue) {
            const config = toJS(cacheValue.item);
            config[propertyToMapInConfig].products = products;
            _set(config, [propertyToMapInConfig], config[propertyToMapInConfig]);
            requestCache.put({ typeName: MODULE_CONFIG_KEY, key: moduleId }, { item: config });
        }
    }
};
